
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/hooks/useAuth";
import { ScrollToTop } from "@/components/ScrollToTop";
import FrenchRedirect from "@/components/FrenchRedirect";
import { VisitorTracker } from "@/components/VisitorTracker";
import { CookieConsent } from "@/components/CookieConsent";

import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Blog from "./pages/Blog";
import BlogAutoUpdate from "./pages/BlogAutoUpdate";

import BlogRepsCRMs from "./pages/BlogRepsCRMs";
import BlogAICopilots from "./pages/BlogAICopilots";
import BlogDataQuality from "./pages/BlogDataQuality";
import BlogHubSpotSalesforce from "./pages/BlogHubSpotSalesforce";
import BlogVoiceROI from "./pages/BlogVoiceROI";
import BlogSMBsCopilot from "./pages/BlogSMBsCopilot";
import BlogAutoUpdate2025 from "./pages/BlogAutoUpdate2025";
import BlogGDPRCopilot from "./pages/BlogGDPRCopilot";
import BlogCRMAutomation from "./pages/BlogCRMAutomation";
import PrivacyEU from "./pages/PrivacyEU";
import Comparison from "./pages/Comparison";
import ComparisonSalesforce from "./pages/ComparisonSalesforce";
import ComparisonHubSpot from "./pages/ComparisonHubSpot";
import AIAgentSalesforce from "./pages/AIAgentSalesforce";
import TryApp from "./pages/TryApp";
import ForAI from "./pages/ForAI";
import Documentation from "./pages/Documentation";
import TechnicalFAQ from "./pages/TechnicalFAQ";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DPA from "./pages/DPA";
import Subprocessors from "./pages/Subprocessors";
import Security from "./pages/Security";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminVisitors from "./pages/AdminVisitors";
import About from "./pages/About";
import Pricing from "./pages/Pricing";
import Trial from "./pages/Trial";
import Integrations from "./pages/Integrations";
import UseCasePage from "./pages/UseCases";
import IntegrationDetailPage from "./pages/IntegrationDetail";
import ProductPlatform from "./pages/ProductPlatform";

import CommandCenter from "./pages/CommandCenter";
import AgenticAISales from "./pages/AgenticAISales";
import AIWorkflowAutomation from "./pages/AIWorkflowAutomation";
import AIForCROs from "./pages/AIForCROs";
import LeadMagnet from "./pages/LeadMagnet";
import CRMAgent from "./pages/CRMAgent";
import CRMEnrichmentAgent from "./pages/CRMEnrichmentAgent";
import CRMDataEntryAutomation from "./pages/CRMDataEntryAutomation";
import WhyCRMDataIsBroken from "./pages/WhyCRMDataIsBroken";
import NoQ5VsRelevanceAI from "./pages/NoQ5VsRelevanceAI";
import NoQ5VsAgentforce from "./pages/NoQ5VsAgentforce";
import NoQ5VsHubSpotAI from "./pages/NoQ5VsHubSpotAI";
import PipelineManagementAI from "./pages/PipelineManagementAI";
import LeadQualificationAI from "./pages/LeadQualificationAI";
import HubSpotAIAgent from "./pages/HubSpotAIAgent";
import PipedriveAIAgent from "./pages/PipedriveAIAgent";
import AICRMAutomation from "./pages/AICRMAutomation";
import HowToUseAIInSales from "./pages/HowToUseAIInSales";
import AISalesOps from "./pages/AISalesOps";
import ReplaceCRMManualWork from "./pages/ReplaceCRMManualWork";
import ZohoAIAgent from "./pages/ZohoAIAgent";
import ApolloVsNoQ5 from "./pages/ApolloVsNoQ5";
import ZoomInfoVsNoQ5 from "./pages/ZoomInfoVsNoQ5";
import AutomateLeadEnrichment from "./pages/AutomateLeadEnrichment";
import AutomateCRMUpdates from "./pages/AutomateCRMUpdates";
import AutomateSalesNotes from "./pages/AutomateSalesNotes";
import AutomateFollowUps from "./pages/AutomateFollowUps";
import AutomateDealTracking from "./pages/AutomateDealTracking";
import AutomateContactSync from "./pages/AutomateContactSync";
import AutomateDataCleaning from "./pages/AutomateDataCleaning";
import AICRMForSaaS from "./pages/AICRMForSaaS";
import AICRMForRecruitment from "./pages/AICRMForRecruitment";
import AICRMForRealEstate from "./pages/AICRMForRealEstate";
import AICRMForFintech from "./pages/AICRMForFintech";
import AICRMForHealthcare from "./pages/AICRMForHealthcare";
import AutoUpdateSalesforce from "./pages/AutoUpdateSalesforce";
import FillCRMDataAutomatically from "./pages/FillCRMDataAutomatically";
import AIAutoLogCalls from "./pages/AIAutoLogCalls";
import SyncCRMWithEmail from "./pages/SyncCRMWithEmail";
import BestAISalesAssistants2026 from "./pages/BestAISalesAssistants2026";
import BestSalesForecastingSoftware2026 from "./pages/BestSalesForecastingSoftware2026";
import RevenueIntelligence from "./pages/RevenueIntelligence";
import SalesForecastingSoftware from "./pages/SalesForecastingSoftware";
import DealIntelligence from "./pages/DealIntelligence";
import AIChiefOfStaffForRevenue from "./pages/AIChiefOfStaffForRevenue";
import SecurityPrivacyFAQ from "./pages/SecurityPrivacyFAQ";
import AiSdr from "./pages/AiSdr";
import OAuthConsent from "./pages/OAuthConsent";
import CrmOptional from "./pages/CrmOptional";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <VisitorTracker />
          <CookieConsent />

          <AnimatePresence mode="wait">
            <Routes>
              {/* English Routes */}
              <Route path="/" element={<Index />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/how-to-update-salesforce-automatically" element={<BlogAutoUpdate />} />
              <Route path="/blog/why-sales-reps-hate-crms" element={<BlogRepsCRMs />} />
              <Route path="/blog/ai-copilots-sales-teams-2025" element={<BlogAICopilots />} />
              <Route path="/blog/salesforce-data-quality-ai-voice-assistants" element={<BlogDataQuality />} />
              <Route path="/blog/hubspot-vs-salesforce-ai-integration-2025" element={<BlogHubSpotSalesforce />} />
              <Route path="/blog/voice-assistant-technology-sales-teams-roi-calculator" element={<BlogVoiceROI />} />
              <Route path="/blog/best-salesforce-ai-copilot-smbs-2025" element={<BlogSMBsCopilot />} />
              <Route path="/blog/update-salesforce-automatically-2025" element={<BlogAutoUpdate2025 />} />
              <Route path="/blog/gdpr-compliant-salesforce-ai-copilot-europe" element={<BlogGDPRCopilot />} />
              <Route path="/blog/crm-automation-guide" element={<BlogCRMAutomation />} />
              <Route path="/blog/comprehensive-guide-to-crm-automation" element={<Navigate to="/blog/crm-automation-guide" replace />} />
              <Route path="/privacy-eu" element={<PrivacyEU />} />
              <Route path="/comparison" element={<Comparison />} />
              <Route path="/comparison/salesforce-einstein" element={<ComparisonSalesforce />} />
              <Route path="/comparison/hubspot" element={<ComparisonHubSpot />} />
              <Route path="/ai-agent-salesforce" element={<AIAgentSalesforce />} />
              <Route path="/app" element={<TryApp />} />
              <Route path="/for-ai" element={<ForAI />} />
              <Route path="/docs" element={<Documentation />} />
              <Route path="/faq" element={<TechnicalFAQ />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/visitors" element={<AdminVisitors />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/dpa" element={<DPA />} />
              <Route path="/subprocessors" element={<Subprocessors />} />
              <Route path="/security" element={<Security />} />
              <Route path="/security-privacy-faq" element={<SecurityPrivacyFAQ />} />
              <Route path="/security-faq" element={<Navigate to="/security-privacy-faq" replace />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/integrations/:slug" element={<IntegrationDetailPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/trial" element={<Trial />} />
              <Route path="/product/platform" element={<ProductPlatform />} />
              
              <Route path="/product/command-center" element={<CommandCenter />} />
              <Route path="/agentic-ai-sales" element={<AgenticAISales />} />
              <Route path="/ai-workflow-automation" element={<AIWorkflowAutomation />} />
              <Route path="/ai-for-cros" element={<AIForCROs />} />
              <Route path="/use-cases" element={<UseCasePage />} />
              <Route path="/resources/cro-pipeline-playbook" element={<LeadMagnet />} />
              <Route path="/crm-agent" element={<CRMAgent />} />
              <Route path="/crm-ai-agent" element={<Navigate to="/crm-agent" replace />} />
              <Route path="/crm-enrichment-agent" element={<CRMEnrichmentAgent />} />
              <Route path="/crm-data-entry-automation" element={<CRMDataEntryAutomation />} />
              <Route path="/why-crm-data-is-broken" element={<WhyCRMDataIsBroken />} />
              <Route path="/noq5-vs-relevance-ai" element={<NoQ5VsRelevanceAI />} />
              <Route path="/noq5-vs-agentforce" element={<NoQ5VsAgentforce />} />
              <Route path="/noq5-vs-hubspot-ai" element={<NoQ5VsHubSpotAI />} />
              <Route path="/pipeline-management-ai" element={<PipelineManagementAI />} />
              <Route path="/lead-qualification-ai" element={<LeadQualificationAI />} />
              <Route path="/hubspot-ai-agent" element={<HubSpotAIAgent />} />
              <Route path="/pipedrive-ai-agent" element={<PipedriveAIAgent />} />
              <Route path="/ai-crm-automation" element={<AICRMAutomation />} />
              <Route path="/how-to-use-ai-in-sales" element={<HowToUseAIInSales />} />
              <Route path="/ai-sales-ops" element={<AISalesOps />} />
              <Route path="/replace-crm-manual-work" element={<ReplaceCRMManualWork />} />
              <Route path="/zoho-ai-agent" element={<ZohoAIAgent />} />
              <Route path="/apollo-vs-noq5" element={<ApolloVsNoQ5 />} />
              <Route path="/zoominfo-vs-noq5" element={<ZoomInfoVsNoQ5 />} />
              <Route path="/crm-ai-agents" element={<Navigate to="/crm-agent" replace />} />
              <Route path="/automate-lead-enrichment" element={<AutomateLeadEnrichment />} />
              <Route path="/automate-crm-updates" element={<AutomateCRMUpdates />} />
              <Route path="/automate-sales-notes" element={<AutomateSalesNotes />} />
              <Route path="/automate-follow-ups" element={<AutomateFollowUps />} />
              <Route path="/automate-deal-tracking" element={<AutomateDealTracking />} />
              <Route path="/automate-contact-sync" element={<AutomateContactSync />} />
              <Route path="/automate-data-cleaning" element={<AutomateDataCleaning />} />
              <Route path="/ai-crm-for-saas" element={<AICRMForSaaS />} />
              <Route path="/ai-crm-for-recruitment" element={<AICRMForRecruitment />} />
              <Route path="/ai-crm-for-real-estate" element={<AICRMForRealEstate />} />
              <Route path="/ai-crm-for-fintech" element={<AICRMForFintech />} />
              <Route path="/ai-crm-for-healthcare" element={<AICRMForHealthcare />} />
              <Route path="/auto-update-salesforce" element={<AutoUpdateSalesforce />} />
              <Route path="/fill-crm-data-automatically" element={<FillCRMDataAutomatically />} />
              <Route path="/auto-log-calls-crm" element={<AIAutoLogCalls />} />
              <Route path="/sync-crm-with-email" element={<SyncCRMWithEmail />} />
              <Route path="/best-ai-sales-assistants-2026" element={<BestAISalesAssistants2026 />} />
              <Route path="/best-ai-sales-assistant" element={<Navigate to="/best-ai-sales-assistants-2026" replace />} />
              <Route path="/revenue-intelligence" element={<RevenueIntelligence />} />
              <Route path="/revenue-intelligence-platform" element={<Navigate to="/revenue-intelligence" replace />} />
              <Route path="/sales-forecasting-software" element={<SalesForecastingSoftware />} />
              <Route path="/ai-sales-forecasting" element={<Navigate to="/sales-forecasting-software" replace />} />
              <Route path="/best-sales-forecasting-software-2026" element={<BestSalesForecastingSoftware2026 />} />
              <Route path="/best-sales-forecasting-software" element={<Navigate to="/best-sales-forecasting-software-2026" replace />} />
              <Route path="/aviso-alternatives" element={<Navigate to="/best-sales-forecasting-software-2026" replace />} />
              <Route path="/sales-forecasting-software-comparison" element={<Navigate to="/best-sales-forecasting-software-2026" replace />} />
              <Route path="/deal-intelligence" element={<DealIntelligence />} />
              <Route path="/ai-chief-of-staff-for-revenue" element={<AIChiefOfStaffForRevenue />} />
              <Route path="/ai-chief-of-staff" element={<Navigate to="/ai-chief-of-staff-for-revenue" replace />} />
              <Route path="/ai-sdr" element={<AiSdr />} />
              <Route path="/ai-sdr-software" element={<Navigate to="/ai-sdr" replace />} />
              <Route path="/ai-sales-agent" element={<Navigate to="/ai-sdr" replace />} />
              <Route path="/crm-optional" element={<CrmOptional />} />
              <Route path="/no-crm" element={<Navigate to="/crm-optional" replace />} />
              <Route path="/without-crm" element={<Navigate to="/crm-optional" replace />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="/use-cases/:slug" element={<UseCasePage />} />
              
              {/* French routes redirect to English equivalents */}
              <Route path="/fr/*" element={<FrenchRedirect />} />
              <Route path="/fr" element={<FrenchRedirect />} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AnimatePresence>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
